/**
 * StripeCheckout — wraps Stripe Elements for wallet top-up.
 * Props:
 *   amountTND  {number}   Amount in Tunisian Dinar
 *   onSuccess  {function} Called after wallet is credited
 *   onError    {function} Called with error message string
 */
import React, { useState, useEffect } from 'react';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import axios from 'axios';

/* ── Card form (inner — must be inside <Elements>) ─────────── */
function CardForm({ amountTND, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [cardError, setCardError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setCardError('');

    try {
      /* 1. Create PaymentIntent on server */
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        '/api/payments/stripe/create-intent',
        { amountTND: Number(amountTND) },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!data.success) throw new Error(data.message || 'Failed to create payment intent');

      /* 2. Confirm card payment with Stripe.js */
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        data.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        },
      );

      if (error) {
        setCardError(error.message || 'Card payment failed');
        onError && onError(error.message);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        /* 3. Confirm top-up server-side (fallback if webhook hasn't fired yet) */
        await axios.post(
          '/api/payments/stripe/confirm-topup',
          { paymentIntentId: paymentIntent.id, amountTND: Number(amountTND) },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        onSuccess && onSuccess({ amountTND, paymentIntentId: paymentIntent.id });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Payment failed';
      setCardError(msg);
      onError && onError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {/* Stripe CardElement container */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          p: 2,
          mb: 2,
          background: (t) => t.palette.background.paper,
          '&:focus-within': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(108,99,255,0.15)' },
          transition: 'border-color 0.2s, box-shadow 0.2s',
        }}
      >
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                letterSpacing: '0.025em',
                fontFamily: '"Inter", sans-serif',
                '::placeholder': { color: '#aab7c4' },
              },
              invalid: { color: '#f44336' },
            },
            hidePostalCode: true,
          }}
        />
      </Box>

      {cardError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
          {cardError}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={!stripe || loading}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
        sx={{ borderRadius: '25px', py: 1.5, fontWeight: 700, fontSize: '1rem' }}
      >
        {loading ? 'Processing…' : `Pay ${amountTND} TND`}
      </Button>

      <Typography
        variant="caption"
        color="text.secondary"
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={0.5}
        mt={1.5}
      >
        <LockIcon sx={{ fontSize: 13 }} /> Secured by Stripe
      </Typography>
    </Box>
  );
}

/* ── Public component (provides Stripe context) ────────────── */
export default function StripeCheckout({ amountTND, onSuccess, onError }) {
  const [stripePromise, setStripePromise] = useState(null);
  const [settingsError, setSettingsError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get('/api/payment-settings/public', { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (!data.stripe?.enabled) {
          setSettingsError('Stripe payments are currently disabled.');
          return;
        }
        if (!data.stripe?.publishableKey) {
          setSettingsError('Stripe is not configured yet. Please contact support.');
          return;
        }
        setStripePromise(loadStripe(data.stripe.publishableKey));
      })
      .catch(() => setSettingsError('Failed to load payment configuration.'));
  }, []);

  if (settingsError) {
    return <Alert severity="warning" sx={{ borderRadius: '12px' }}>{settingsError}</Alert>;
  }
  if (!stripePromise) {
    return <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>;
  }

  return (
    <Elements stripe={stripePromise}>
      <CardForm amountTND={amountTND} onSuccess={onSuccess} onError={onError} />
    </Elements>
  );
}
